import { apiManager } from '@/api/ApiManager';
import OnboardingFlow from '@/components/OnboardingFlow';
import SessionExpiredModal from '@/components/SessionExpiredModal';
import { AuthContext, type AuthContextType } from '@/contexts/AuthContext';
import { useInactivity } from '@/hooks/useInactivity';
import { authService } from '@/services/AuthService';
import React, { useEffect, useState, type ReactNode } from 'react';
import { UserRole, type LoginRequest, type LoginResponse } from '../../types/ApiTypes';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<LoginResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSessionExpired, setShowSessionExpired] = useState(() => {
    return sessionStorage.getItem('session_expired') === 'true';
  });
  const [sessionExpiredReason, setSessionExpiredReason] = useState<'unauthorized' | 'inactivity'>(() => {
    return (sessionStorage.getItem('session_expired_reason') as 'unauthorized' | 'inactivity') || 'unauthorized';
  });

  const handleInactivity = () => {
    sessionStorage.setItem('session_expired', 'true');
    sessionStorage.setItem('session_expired_reason', 'inactivity');
    setSessionExpiredReason('inactivity');
    setShowSessionExpired(true);
  };

  const { resetTimer } = useInactivity(handleInactivity, !!user);

  useEffect(() => {
    apiManager.setUnauthorizedHandler(() => {
      setUser(null);
      localStorage.removeItem('admin_user');
      localStorage.removeItem('admin_token');
      sessionStorage.setItem('session_expired', 'true');
      sessionStorage.setItem('session_expired_reason', 'unauthorized');
      setSessionExpiredReason('unauthorized');
      setShowSessionExpired(true);
    });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const userData = localStorage.getItem('admin_user');

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
      }
    }

    setIsLoading(false);
  }, []);

  const login = async (credentials: LoginRequest) => {
    try {
      const response = await authService.login(credentials);

      if (response.status === 'success' && response.data) {
        sessionStorage.removeItem('session_expired');
        sessionStorage.removeItem('session_expired_reason');
        setUser(response.data);
        localStorage.setItem('admin_user', JSON.stringify(response.data));
        if (response.data.token) {
          localStorage.setItem('admin_token', response.data.token);
        }
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_token');
  };

  const refreshUser = async () => {
    try {
      const response = await authService.getMe();
      if (response.status === 'success' && response.data && user?.token) {
        const updatedUser: LoginResponse = { ...response.data, token: user.token };
        setUser(updatedUser);
        localStorage.setItem('admin_user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  const hasRole = (role: UserRole): boolean => {
    if (!user) return false;

    if (role === UserRole.SUPERADMIN) {
      return user.role === UserRole.SUPERADMIN;
    }

    if (role === UserRole.ADMIN) {
      return user.role === UserRole.ADMIN || user.role === UserRole.SUPERADMIN;
    }

    return user.role === role;
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
    hasRole,
    isSuperAdmin: hasRole(UserRole.SUPERADMIN),
    isAdmin: hasRole(UserRole.ADMIN),
  };

  const handleVerifyPin = () => {
    setShowSessionExpired(false);
    window.dispatchEvent(new CustomEvent('verify-pin-request'));

    const handlePinVerified = () => {
      sessionStorage.removeItem('session_expired');
      sessionStorage.removeItem('session_expired_reason');
      resetTimer();
      window.removeEventListener('pin-verified', handlePinVerified);
    };

    window.addEventListener('pin-verified', handlePinVerified);
  };

  const handleLoginAgain = () => {
    sessionStorage.removeItem('session_expired');
    sessionStorage.removeItem('session_expired_reason');
    setShowSessionExpired(false);
    setUser(null);
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_token');
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <OnboardingFlow />
      <SessionExpiredModal
        open={showSessionExpired}
        showPinOption={sessionExpiredReason === 'inactivity'}
        onVerifyPin={handleVerifyPin}
        onLoginAgain={handleLoginAgain}
      />
    </AuthContext.Provider>
  );
};

